import { ZfFormField, ZfInput, ZfSelectOption, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CashFlowType } from '../../../api/generated/client';
import type { IAdvisoryOrLifeEventFormValues } from '../../../interfaces/event';
import { requiredField } from '../../../utils/form-utils';

interface IProps {
    currency?: string;
    isCashFlowRelevant: boolean;
    control: Control<IAdvisoryOrLifeEventFormValues>;
    setFormValue: UseFormSetValue<IAdvisoryOrLifeEventFormValues>;
}

export const NeedEventModalTypeAmountFields: FunctionComponent<IProps> = ({ currency, isCashFlowRelevant, control, setFormValue }) => {
    const { t } = useTranslation();

    const typeOptions = [
        { value: 'INFLOW', label: t('inflow') },
        { value: 'OUTFLOW', label: t('outflow') },
        { value: 'NON_CASH_FLOW', label: t('nonCashflowEvent') },
    ];

    return (
        <div className="d-flex gap-16 w-100">
            <Controller
                name="type"
                control={control}
                rules={{ required: requiredField(t('selectFieldRequired')) }}
                render={({ field, fieldState }) => (
                    <ZfFormField
                        label={t('eventModalType')}
                        mandatory={true}
                        className="half-width-gap-16"
                        alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                    >
                        <ZfInput
                            type="select"
                            value={field.value}
                            error={fieldState.invalid}
                            onSelectChange={(e) => {
                                field.onChange(e.detail as CashFlowType);
                                setFormValue('isCashFlowRelevant', e.detail !== 'NON_CASH_FLOW');
                            }}
                            onBlur={field.onBlur}
                        >
                            {typeOptions.map((typeOption) => (
                                <ZfSelectOption key={typeOption.value} value={typeOption.value} description={typeOption.label} />
                            ))}
                        </ZfInput>
                    </ZfFormField>
                )}
            />
            <Controller
                name="amount"
                control={control}
                rules={{ required: isCashFlowRelevant ? requiredField(t('textFieldRequired')) : undefined }}
                render={({ field, fieldState }) => (
                    <ZfFormField
                        label={t('wealthItemAmount')}
                        mandatory={isCashFlowRelevant}
                        className="half-width-gap-16"
                        alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                    >
                        <ZfInput
                            value={field.value}
                            type="number"
                            placeholder="0,000.00"
                            format={true}
                            disabled={!isCashFlowRelevant}
                            error={fieldState.invalid}
                            onInputChange={(e) => field.onChange(e.detail != null ? Number.parseFloat(e.detail.replaceAll(',', '')) : undefined)}
                            onBlur={field.onBlur}
                        >
                            <ZfTypography slot="leading" variant="label-medium">
                                {currency}
                            </ZfTypography>
                        </ZfInput>
                    </ZfFormField>
                )}
            />
        </div>
    );
};
